import { OrderRequestSchema, Product } from '../../../types';
import { CHANGE_REQUEST, isPointToPoint, NEW_SERVICE_REQUEST } from './serviceRequestModel';

export const buildRequestSchema = (
  orderRequestSchema: OrderRequestSchema | null,
  requestType: string,
  productSelection: Product | null
): OrderRequestSchema | null => {
  if (!orderRequestSchema) return null;

  const clonedSchema = JSON.parse(JSON.stringify(orderRequestSchema));
  if (requestType === NEW_SERVICE_REQUEST) {
    if (clonedSchema.schema.validation_schema.properties) {
      delete clonedSchema.schema.validation_schema.properties.existing_circuit_details;
    }
    if (clonedSchema.schema.ui_schema) {
      delete clonedSchema.schema.ui_schema.existing_circuit_details;
      if (clonedSchema.schema.ui_schema['ui:options']?.order) {
        clonedSchema.schema.ui_schema['ui:options'].order = clonedSchema.schema.ui_schema['ui:options'].order.filter(
          (field: string) => field !== 'existing_circuit_details'
        );
      }
    }
  }
  if (requestType === CHANGE_REQUEST) {
    const fieldsToRemove = [
      'desired_install_date',
      'ips',
      'ip_requirements',
      'cer_per',
      'speed',
      'location_a',
      'location_z',
      'diversifying_from',
      'handoff_requirement',
      'existing_circuit_details',
      'billing_same_as_install',
      'billing_address'
    ];
    const valSchema = clonedSchema.schema.validation_schema;
    const uiSchema = clonedSchema.schema.ui_schema;
    fieldsToRemove.forEach((field) => {
      if (valSchema.properties) delete valSchema.properties[field];
      if (uiSchema) delete uiSchema[field];
    });
    if (valSchema.required) {
      valSchema.required = valSchema.required.filter((f: string) => !fieldsToRemove.includes(f));
    }
    // Relabelled rather than renamed: the portal reads this request back under the schema's own key.
    if (valSchema.properties && valSchema.properties.location_delivery_details) {
      if (uiSchema && uiSchema.location_delivery_details) {
        uiSchema.location_delivery_details = {
          ...uiSchema.location_delivery_details,
          'ui:title': 'Change Request Details',
          'ui:placeholder': 'ie: increase bandwidth?'
        };
      }
      if (uiSchema['ui:options']?.order) {
        uiSchema['ui:options'].order = uiSchema['ui:options'].order.filter((f: string) => !fieldsToRemove.includes(f));
      }
    }
  }

  if (productSelection) {
    const productName = productSelection.name || '';
    const isP2P = isPointToPoint(productSelection);
    const isDIA = /dedicated internet access/i.test(productName) || productName === 'DIA';
    const isBroadband = /broadband/i.test(productName);
    const isMPLS = /multi.?protocol.?label.?switching|mpls/i.test(productName);
    const isSpeedApplicable = isDIA || isBroadband || isP2P || isMPLS;

    const valSchema = clonedSchema.schema.validation_schema;
    const uiSchema = clonedSchema.schema.ui_schema;

    const removeFields = (fields: string[]) => {
      fields.forEach((field) => {
        if (valSchema.properties) delete valSchema.properties[field];
        if (uiSchema) delete uiSchema[field];
        if (uiSchema['ui:options']?.order) {
          uiSchema['ui:options'].order = uiSchema['ui:options'].order.filter((f: string) => f !== field);
        }
        if (valSchema.required) {
          valSchema.required = valSchema.required.filter((f: string) => f !== field);
        }
      });
    };

    if (!isSpeedApplicable) removeFields(['speed']);

    if (isDIA) {
      removeFields(['ips', 'cer_per']);
    } else if (isMPLS) {
      removeFields(['ips', 'ip_requirements']);
    } else if (isP2P) {
      removeFields(['ips', 'ip_requirements', 'cer_per']);
    } else {
      removeFields(['ip_requirements', 'cer_per']);
    }
  }

  return clonedSchema;
};
